import apiClient from './client';
import { resolveRouteContextScope } from '@/lib/route-context-scope';
import { useAppStore } from '@/stores/appStore';
import { queryClient } from '@/app/providers/query-client';
import type { QueryClient } from '@tanstack/react-query';
import type { ScreenplayCoordination, ScreenplayWorkflowSnapshot } from '@/types/ai-session';

export type { ScreenplayCoordination } from '@/types/ai-session';

// 仅保留当前浏览器上下文的凭据，既不跨用户持久化，也不自动领取。
const coordinationStates = new Map<string, ScreenplayCoordination>();
const sessionScreenplays = new Map<string, string>();

export function getScreenplayCoordinationIdentity(): string {
  const userId = useAppStore.getState().user?.userId ?? 'anonymous';
  const route = resolveRouteContextScope(
    typeof window === 'undefined' ? '' : (window.location?.pathname ?? '')
  );
  const orgId =
    route.type === 'org'
      ? route.orgId
      : route.type === 'personal' || typeof sessionStorage === 'undefined'
        ? null
        : sessionStorage.getItem('active_org_id');
  return `${userId}:${orgId || 'personal'}`;
}

export function rememberScreenplaySession(
  sessionId: string,
  screenplayId: string,
  identity: string
): void {
  sessionScreenplays.set(`${identity}:${sessionId}`, screenplayId);
}

export function getScreenplayOwnershipHeaders(screenplayId: string): Record<string, string> {
  const state = coordinationStates.get(`${getScreenplayCoordinationIdentity()}:${screenplayId}`);
  const version = state?.ownershipVersion;
  return version === undefined ? {} : { 'X-Screenplay-Ownership-Version': String(version) };
}

function getAISessionScreenplayId(url: string, data?: unknown): string | undefined {
  const workflowId = url.match(/^\/screenplay-workflows\/([^/]+)/)?.[1];
  const sessionId = url.match(/^\/ai-sessions\/([^/]+)/)?.[1];
  const sourceId =
    data && typeof data === 'object' && 'sourceId' in data && typeof data.sourceId === 'string'
      ? data.sourceId
      : undefined;
  const sourceSessionId =
    data &&
    typeof data === 'object' &&
    'sourceSessionId' in data &&
    typeof data.sourceSessionId === 'string'
      ? data.sourceSessionId
      : undefined;
  const screenplayId =
    workflowId ??
    sourceId ??
    (sessionId
      ? sessionScreenplays.get(`${getScreenplayCoordinationIdentity()}:${sessionId}`)
      : undefined) ??
    (sourceSessionId
      ? sessionScreenplays.get(`${getScreenplayCoordinationIdentity()}:${sourceSessionId}`)
      : undefined);
  return screenplayId;
}

export function getAISessionOwnershipHeaders(url: string, data?: unknown): Record<string, string> {
  const screenplayId = getAISessionScreenplayId(url, data);
  return screenplayId ? getScreenplayOwnershipHeaders(screenplayId) : {};
}

// 成功响应已携带执行权；仅失败时按需刷新聚合快照，恢复可能已提交的领取。
export async function withAISessionCoordination<T>(
  url: string,
  data: unknown,
  request: () => Promise<T>
): Promise<T> {
  const identity = getScreenplayCoordinationIdentity();
  const screenplayId = getAISessionScreenplayId(url, data);
  try {
    return await request();
  } catch (error) {
    if (screenplayId && identity === getScreenplayCoordinationIdentity()) {
      await queryClient
        .invalidateQueries({
          queryKey: ['screenplay-workflow-snapshot', screenplayId, identity],
          refetchType: 'all',
        })
        .catch(() => undefined);
    }
    throw error;
  }
}

async function rememberResponse(
  request: Promise<unknown>,
  identity: string
): Promise<ScreenplayCoordination> {
  const state = (await request) as ScreenplayCoordination;
  return rememberScreenplayCoordination(state, identity);
}

export function rememberScreenplayCoordination(
  state: ScreenplayCoordination,
  identity: string
): ScreenplayCoordination {
  const key = `${identity}:${state.screenplayId}`;
  const previous = coordinationStates.get(key);
  // 接管或释放后的迟到轮询不能把页面和写凭据退回旧版本。
  if (previous && previous.ownershipVersion > state.ownershipVersion) return previous;
  coordinationStates.set(key, state);
  return state;
}

/** 在统一响应解包前消费执行权元数据，更新原请求身份的已有快照。 */
export function applyCoordinationResponse(
  queryClient: QueryClient,
  metadata: { identity: string; state: ScreenplayCoordination }
): void {
  const { identity, state } = metadata;
  const queryKey = ['screenplay-workflow-snapshot', state.screenplayId, identity];
  const snapshot = queryClient.getQueryData<ScreenplayWorkflowSnapshot>(queryKey);
  if (snapshot?.coordination) rememberScreenplayCoordination(snapshot.coordination, identity);
  const coordination = rememberScreenplayCoordination(state, identity);
  queryClient.setQueryData<ScreenplayWorkflowSnapshot>(queryKey, (previous) =>
    previous ? { ...previous, coordination } : undefined
  );
}

export const screenplayCoordinationApi = {
  revoke(screenplayId: string, ownershipVersion: number) {
    const identity = getScreenplayCoordinationIdentity();
    return rememberResponse(
      apiClient.post(`/screenplay-workflows/${screenplayId}/coordination/revoke`, {
        ownershipVersion,
      }),
      identity
    );
  },
  release(screenplayId: string, ownershipVersion: number) {
    const identity = getScreenplayCoordinationIdentity();
    return rememberResponse(
      apiClient.post(`/screenplay-workflows/${screenplayId}/coordination/release`, {
        ownershipVersion,
      }),
      identity
    );
  },
  takeover(screenplayId: string, ownershipVersion: number) {
    const identity = getScreenplayCoordinationIdentity();
    return rememberResponse(
      apiClient.post(`/screenplay-workflows/${screenplayId}/coordination/takeover`, {
        ownershipVersion,
      }),
      identity
    );
  },
};
